import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Instagram, Facebook, Youtube, MessageCircle, Users, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteInfo } from '../data/siteData';

export const Unisciti = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="unisciti-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#002060] via-[#003380] to-[#002060] py-20 md:py-32 overflow-hidden">
        {/* Animated background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#5CB9E5]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFD700]/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#5CB9E5]/5 rounded-full blur-3xl animate-ping" style={{ animationDuration: '3s' }}></div>
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-[#FFD700]/40 rounded-full animate-bounce"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${2 + i * 0.5}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Torna alla home
          </Link>

          <div className="relative inline-block mb-8 animate-fade-in-up">
            <div className="absolute inset-0 bg-[#5CB9E5]/30 rounded-full blur-xl animate-pulse"></div>
            <img 
              src={siteInfo.logo}
              alt={siteInfo.name}
              className="relative w-28 h-28 md:w-36 md:h-36 rounded-full shadow-2xl hover:scale-105 transition-transform duration-500"
            />
          </div>
          
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mb-6 animate-fade-in-up animation-delay-100">
            Unisciti a Noi
          </h1>
          
          <p className="text-xl md:text-2xl text-[#5CB9E5] font-medium mb-4 animate-fade-in-up animation-delay-200">
            Entra a far parte de La Renaissance Valdôtaine
          </p>
          
          <p className="text-white/80 text-lg max-w-2xl mx-auto animate-fade-in-up animation-delay-300">
            Crediamo nel cambiamento e nella partecipazione attiva di ogni cittadino. 
            Se condividi i nostri valori e vuoi contribuire alla rinascita della Valle d'Aosta, 
            contattaci!
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Come Unirsi Section */}
      <section className="py-16 md:py-24 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-4 block animate-fade-in">
              Come fare
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#002060] mb-4">
              È semplice unirti al nostro progetto
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Scegli il modo che preferisci per metterti in contatto con noi. 
              Saremo felici di conoscerti e raccontarti di più sul nostro movimento.
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {/* Telefono */}
            <a 
              href="tel:+393701556555"
              className="group bg-[#F4F6F8] rounded-2xl p-8 text-center hover:bg-[#002060] transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl"
              data-testid="contact-phone"
            >
              <div className="w-16 h-16 bg-[#002060] group-hover:bg-[#5CB9E5] rounded-full mx-auto mb-6 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-12">
                <Phone className="w-8 h-8 text-[#FFD700] group-hover:text-[#002060] transition-colors" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#002060] group-hover:text-white mb-2 transition-colors">
                Chiamaci o scrivici
              </h3>
              <p className="text-slate-600 group-hover:text-white/80 mb-4 transition-colors">
                WhatsApp o telefono
              </p>
              <span className="text-2xl font-bold text-[#5CB9E5] group-hover:text-[#FFD700] transition-colors">
                +39 370 155 6555
              </span>
            </a>

            {/* Email */}
            <a 
              href="mailto:larenaissancevaldotaine@gmail.com"
              className="group bg-[#F4F6F8] rounded-2xl p-8 text-center hover:bg-[#002060] transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl"
              data-testid="contact-email"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="w-16 h-16 bg-[#002060] group-hover:bg-[#5CB9E5] rounded-full mx-auto mb-6 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-12">
                <Mail className="w-8 h-8 text-[#FFD700] group-hover:text-[#002060] transition-colors" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#002060] group-hover:text-white mb-2 transition-colors">
                Scrivici una mail
              </h3>
              <p className="text-slate-600 group-hover:text-white/80 mb-4 transition-colors">
                Ti risponderemo al più presto
              </p>
              <span className="text-lg font-bold text-[#5CB9E5] group-hover:text-[#FFD700] transition-colors break-all">
                larenaissancevaldotaine@gmail.com
              </span>
            </a>

            {/* Instagram DM */}
            <a 
              href={siteInfo.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[#F4F6F8] rounded-2xl p-8 text-center hover:bg-[#002060] transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl md:col-span-2 lg:col-span-1"
              data-testid="contact-instagram"
              style={{ animationDelay: '0.2s' }}
            >
              <div className="w-16 h-16 bg-[#002060] group-hover:bg-[#5CB9E5] rounded-full mx-auto mb-6 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-12">
                <MessageCircle className="w-8 h-8 text-[#FFD700] group-hover:text-[#002060] transition-colors" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#002060] group-hover:text-white mb-2 transition-colors">
                Contattaci in DM
              </h3>
              <p className="text-slate-600 group-hover:text-white/80 mb-4 transition-colors">
                Scrivici su Instagram
              </p>
              <span className="text-lg font-bold text-[#5CB9E5] group-hover:text-[#FFD700] transition-colors">
                @renaissance.vda
              </span>
            </a>
          </div>

          {/* Perché Unirsi */}
          <div className="relative bg-gradient-to-r from-[#002060] to-[#003380] rounded-2xl p-8 md:p-12 text-white overflow-hidden">
            {/* Animated background */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#5CB9E5]/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FFD700]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold mb-6">
                  Perché unirti a noi?
                </h3>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4 group">
                    <div className="p-2 bg-[#FFD700]/20 rounded-lg group-hover:bg-[#FFD700]/30 transition-colors">
                      <Users className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <span className="pt-1">Fai parte di una comunità che crede nel cambiamento</span>
                  </li>
                  <li className="flex items-start gap-4 group">
                    <div className="p-2 bg-[#FFD700]/20 rounded-lg group-hover:bg-[#FFD700]/30 transition-colors">
                      <Heart className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <span className="pt-1">Contribuisci attivamente al futuro della Valle d'Aosta</span>
                  </li>
                  <li className="flex items-start gap-4 group">
                    <div className="p-2 bg-[#FFD700]/20 rounded-lg group-hover:bg-[#FFD700]/30 transition-colors">
                      <MessageCircle className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <span className="pt-1">La tua voce e le tue idee saranno ascoltate</span>
                  </li>
                </ul>
              </div>
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-[#FFD700]/20 rounded-full blur-xl animate-pulse"></div>
                  <img 
                    src={siteInfo.logo}
                    alt={siteInfo.name}
                    className="relative w-32 h-32 mx-auto mb-4 rounded-full opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>
                <p className="text-[#5CB9E5] font-medium italic text-lg">
                  "Insieme per la rinascita"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Section */}
      <section className="py-16 bg-[#F4F6F8] overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#002060] mb-4">
            Seguici sui social
          </h2>
          <p className="text-slate-600 mb-8">
            Resta aggiornato sulle nostre attività e iniziative
          </p>

          <div className="flex items-center justify-center gap-4">
            <a 
              href={siteInfo.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-white rounded-xl px-6 py-4 shadow-sm hover:shadow-xl hover:bg-[#002060] transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
              data-testid="social-link-facebook"
            >
              <Facebook className="w-8 h-8 text-[#002060] group-hover:text-[#FFD700] transition-colors" />
              <span className="font-semibold text-[#002060] group-hover:text-white hidden sm:block transition-colors">Facebook</span>
            </a>
            
            <a 
              href={siteInfo.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-white rounded-xl px-6 py-4 shadow-sm hover:shadow-xl hover:bg-[#002060] transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
              data-testid="social-link-instagram"
            >
              <Instagram className="w-8 h-8 text-[#002060] group-hover:text-[#FFD700] transition-colors" />
              <span className="font-semibold text-[#002060] group-hover:text-white hidden sm:block transition-colors">Instagram</span>
            </a>
            
            <a 
              href={siteInfo.social.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-white rounded-xl px-6 py-4 shadow-sm hover:shadow-xl hover:bg-[#002060] transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
              data-testid="social-link-youtube"
            >
              <Youtube className="w-8 h-8 text-[#002060] group-hover:text-[#FFD700] transition-colors" />
              <span className="font-semibold text-[#002060] group-hover:text-white hidden sm:block transition-colors">YouTube</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#FFD700] relative overflow-hidden">
        {/* Animated stripes */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, #002060 20px, #002060 40px)',
            animation: 'slide 20s linear infinite'
          }}></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#002060] mb-4">
            Non vediamo l'ora di conoscerti!
          </h2>
          <p className="text-[#002060]/80 mb-8">
            Ogni persona che si unisce a noi rende il nostro movimento più forte
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+393701556555">
              <Button className="btn-primary px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transform hover:scale-105 transition-transform">
                <Phone className="w-5 h-5" />
                Chiama ora
              </Button>
            </a>
            <a href="mailto:larenaissancevaldotaine@gmail.com">
              <Button variant="outline" className="px-8 py-3 rounded-lg font-semibold border-2 border-[#002060] text-[#002060] hover:bg-[#002060] hover:text-white flex items-center gap-2 transform hover:scale-105 transition-all">
                <Mail className="w-5 h-5" />
                Invia email
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Custom animation keyframes */}
      <style>{`
        @keyframes slide {
          0% { transform: translateX(-40px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};
